import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { useDialog } from '@/context/DialogContext';
import {
  TextB, TextItalic, TextUnderline, TextStrikethrough,
  TextHOne, TextHTwo, ListBullets, ListNumbers, Quotes,
  Code, Link as LinkIcon, LinkBreak, ArrowUUpLeft, ArrowUUpRight,
  TextAlignLeft, HighlighterCircle,
} from '@phosphor-icons/react';

function ToolbarButton({ onClick, active, disabled, title, children, testid }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`h-8 w-8 flex items-center justify-center text-sm border border-transparent transition-colors ${
        active
          ? 'bg-[#002FA7] text-white'
          : 'text-foreground hover:bg-muted hover:border-border disabled:opacity-40 disabled:hover:bg-transparent'
      }`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="mx-1 h-5 w-px bg-border self-center" />;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Write something…',
  minHeight = 160,
  editable = true,
  autofocus = false,
  testid = 'rich-text-editor',
}) {
  const { prompt } = useDialog();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    editable,
    autofocus,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Treat truly empty documents as empty string
      const isEmpty = editor.isEmpty;
      onChange?.(isEmpty ? '' : html);
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none focus:outline-none px-4 py-3 leading-relaxed [&_p]:my-2 [&_h1]:font-display [&_h2]:font-display [&_h3]:font-display',
        'data-testid': `${testid}-content`,
      },
    },
  });

  // Sync external value → editor (e.g. reset after submit)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = async () => {
    const previousUrl = editor.getAttributes('link').href || '';
    const url = await prompt({
      title: 'Add link',
      description: 'Enter a URL (leave empty to remove the link).',
      defaultValue: previousUrl,
      placeholder: 'https://example.com',
      confirmText: 'Save link',
    });
    if (url === null) return; // cancelled
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
  };

  return (
    <div
      className="border border-border bg-white"
      data-testid={testid}
    >
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-muted/40 p-1">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold (Ctrl+B)"
          testid="rte-bold"
        >
          <TextB size={16} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic (Ctrl+I)"
          testid="rte-italic"
        >
          <TextItalic size={16} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Underline (Ctrl+U)"
          testid="rte-underline"
        >
          <TextUnderline size={16} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
          title="Strikethrough"
          testid="rte-strike"
        >
          <TextStrikethrough size={16} weight="bold" />
        </ToolbarButton>

        <Divider />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
          testid="rte-h2"
        >
          <TextHOne size={16} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
          testid="rte-h3"
        >
          <TextHTwo size={16} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setParagraph().run()}
          active={editor.isActive('paragraph')}
          title="Paragraph"
          testid="rte-p"
        >
          <TextAlignLeft size={16} weight="bold" />
        </ToolbarButton>

        <Divider />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bullet list"
          testid="rte-ul"
        >
          <ListBullets size={16} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Numbered list"
          testid="rte-ol"
        >
          <ListNumbers size={16} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
          title="Quote"
          testid="rte-quote"
        >
          <Quotes size={16} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          active={editor.isActive('code')}
          title="Inline code"
          testid="rte-code"
        >
          <Code size={16} weight="bold" />
        </ToolbarButton>

        <Divider />

        <ToolbarButton
          onClick={setLink}
          active={editor.isActive('link')}
          title="Add / edit link"
          testid="rte-link"
        >
          <LinkIcon size={16} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={!editor.isActive('link')}
          title="Remove link"
          testid="rte-unlink"
        >
          <LinkBreak size={16} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          title="Clear formatting"
          testid="rte-clear"
        >
          <HighlighterCircle size={16} weight="bold" />
        </ToolbarButton>

        <Divider />

        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (Ctrl+Z)"
          testid="rte-undo"
        >
          <ArrowUUpLeft size={16} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (Ctrl+Y)"
          testid="rte-redo"
        >
          <ArrowUUpRight size={16} weight="bold" />
        </ToolbarButton>
      </div>

      <div style={{ minHeight }} className="text-sm">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
